import { ReactNode, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppBar, Toolbar } from '@mui/material';
import { PATH_APP } from 'routes/paths';
import ModalHeaderDialog from 'components/modal-header-dialog';
import {
  Typography,
  Box,
  CloseButton,
  getSafeAreaTop,
  Stack,
  Theme,
  STANDARD_HEADER_HEIGHT,
} from '@planitgeo/treeplotter-ui-library';
import { ConfirmationConfig } from 'common/infrastructure/dialog/types';
import { BeforeMockModalCloseHandler } from './hooks/useBeforeCloseHandler';

export const MODAL_HEADER_Z_INDEX = 1000;

const modalHeaderStyles = {
  'modal-header-container': {
    boxShadow: 'none',
    height: `${STANDARD_HEADER_HEIGHT}px`,
    borderBottom: 1,
    borderColor: 'divider',
    zIndex: MODAL_HEADER_Z_INDEX,
    top: getSafeAreaTop(),
  },
  'toolbar-container': (theme: Theme) => ({
    height: 1,
    px: { lg: 5 },
    background: `linear-gradient(45deg, ${theme.palette.secondary.main}, ${theme.palette.primary.main})`,
  }),
  'custom-content-container': {
    boxShadow: 'none',
    height: 'auto',
    zIndex: MODAL_HEADER_Z_INDEX,
    top: getSafeAreaTop(),
    backgroundColor: 'transparent',
  },
  // Transparent: the record page paints one fixed gradient backdrop behind the
  // toolbar and the scrolling summary fields so no seam can appear between them
  'custom-content-toolbar': {
    height: 'auto',
    minHeight: 'unset',
    p: 0,
    paddingBottom: 2.5,
    background: 'transparent',
  },
};

type Props = {
  title?: string;
  children?: ReactNode;
  beforeMockModalCloseHandler?: BeforeMockModalCloseHandler;
};

export default function ModalHeader({ title = '', children, beforeMockModalCloseHandler }: Props) {
  const navigate = useNavigate();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmationConfig, setConfirmationConfig] = useState<ConfirmationConfig | null>(null);

  const handleClose = () => {
    const result = beforeMockModalCloseHandler ? beforeMockModalCloseHandler('close') : undefined;

    if (!result) {
      navigate(PATH_APP.inventory);
      return;
    }

    if (result.shouldConfirm) {
      setConfirmationConfig(result);
      setDialogOpen(true);
    } else {
      navigate(PATH_APP.inventory);
    }
  };

  const handleDialogConfirm = () => {
    if (confirmationConfig?.onConfirm) {
      confirmationConfig.onConfirm();
    }
    setDialogOpen(false);
    navigate(PATH_APP.inventory);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const closeButton = (
    <CloseButton
      className="modalHeaderClose"
      onClick={handleClose}
      data-testid="modal_close_button"
      sx={{
        backgroundColor: 'common.white',
        color: 'primary.main',
      }}
    />
  );

  const renderDefault = (
    <>
      <Typography
        variant="h6"
        data-id="modal-header-title"
        sx={{ flexGrow: 1, color: 'common.white' }}
      >
        {title}
      </Typography>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="flex-end"
        spacing={{ xs: 0.5, sm: 1.5 }}
      >
        {closeButton}
      </Stack>
    </>
  );

  const renderCustomContent = (
    <Box sx={{ position: 'relative', width: '100%' }}>
      <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}>{closeButton}</Box>
      {children}
    </Box>
  );

  const hasCustomContent = Boolean(children);

  return (
    <>
      <AppBar
        color="primary"
        sx={
          hasCustomContent
            ? modalHeaderStyles['custom-content-container']
            : modalHeaderStyles['modal-header-container']
        }
      >
        <Toolbar
          sx={
            hasCustomContent
              ? modalHeaderStyles['custom-content-toolbar']
              : modalHeaderStyles['toolbar-container']
          }
        >
          {hasCustomContent ? renderCustomContent : renderDefault}
        </Toolbar>
      </AppBar>
      <ModalHeaderDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        onConfirm={handleDialogConfirm}
        onSave={() => {}}
        confirmationConfig={confirmationConfig}
      />
    </>
  );
}
